import type { CSSProperties } from 'react'
import sparkyFrame1 from '@/assets/brand/sparky_frame1.png'
import './sparky-anim.css'

export type SparkyAnimState =
  | 'idle'
  | 'greeting'
  | 'thinking'
  | 'analyzing'
  | 'processing'
  | 'writing'
  | 'asking'
  | 'success'
  | 'error'
  | 'sleeping'

export interface SparkyAnimProps {
  state?: SparkyAnimState
  /** Component size in pixels (width = height). Defaults to the CSS knob (260). */
  size?: number
  className?: string
}

/**
 * Animated Sparky mascot (VONK animation library). One component, ten
 * agent states switched via `data-state`; `prefers-reduced-motion` is
 * handled in the CSS.
 */
export function SparkyAnim({ state = 'idle', size, className }: SparkyAnimProps) {
  const style = size !== undefined ? ({ '--sparky-size': `${size}px` } as CSSProperties) : undefined

  return (
    <div
      className={className ? `sparky-anim ${className}` : 'sparky-anim'}
      data-state={state}
      style={style}
      aria-hidden="true"
    >
      <div className="sparky-fx sparky-fx--glow" />
      <div className="sparky-fx sparky-fx--ring" />
      <img className="sparky-img" src={sparkyFrame1} alt="" />
      <div className="sparky-fx sparky-fx--sparkles">
        <i />
        <i />
        <i />
        <i />
      </div>
      <div className="sparky-fx sparky-fx--thought">
        <div className="b">
          <span />
          <span />
          <span />
        </div>
      </div>
      <div className="sparky-fx sparky-fx--scan" />
      <div className="sparky-fx sparky-fx--doc">
        <div className="pg">
          <i />
          <i />
          <i />
          <i />
        </div>
      </div>
      <div className="sparky-fx sparky-fx--bubble">
        <div className="q">{state === 'error' ? '!' : '?'}</div>
      </div>
      <div className="sparky-fx sparky-fx--check">
        <svg viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="12" />
          <path d="M6 12.5l4 4 8-9" />
        </svg>
      </div>
      <div className="sparky-fx sparky-fx--zzz">
        <b>Z</b>
        <b>Z</b>
        <b>Z</b>
      </div>
      <div className="sparky-fx sparky-fx--detective">
        <svg className="hat" viewBox="0 0 120 86">
          <path d="M52 20 Q46 2 58 8 Q63 13 62 24 Z" fill="#5e3f22" />
          <path d="M68 20 Q74 2 62 8 Q57 13 58 24 Z" fill="#5e3f22" />
          <circle cx="60" cy="15" r="5" fill="#4d3219" />
          <path d="M16 58 Q18 22 60 20 Q102 22 104 58 Z" fill="#a3713f" />
          <g stroke="#84572f" strokeWidth="2" opacity=".5">
            <line x1="38" y1="26" x2="38" y2="56" />
            <line x1="60" y1="22" x2="60" y2="56" />
            <line x1="82" y1="26" x2="82" y2="56" />
            <line x1="26" y1="40" x2="94" y2="40" />
          </g>
          <path d="M16 52 Q60 64 104 52 L104 60 Q60 72 16 60 Z" fill="#5e3f22" />
          <ellipse cx="60" cy="62" rx="56" ry="12" fill="#8a5e34" />
          <ellipse cx="60" cy="60" rx="56" ry="10" fill="#9c6d3e" />
        </svg>
        <svg className="stache" viewBox="0 0 150 54">
          <path
            d="M75 13 C70 23 58 25 46 23 C32 21 18 19 8 31 C6 41 15 46 25 44 C16 40 17 33 27 31 C37 29 50 31 60 27 C66 25 72 19 75 13 Z"
            fill="#241a12"
          />
          <path
            d="M75 13 C80 23 92 25 104 23 C118 21 132 19 142 31 C144 41 135 46 125 44 C134 40 133 33 123 31 C113 29 100 31 90 27 C84 25 78 19 75 13 Z"
            fill="#241a12"
          />
        </svg>
      </div>
      <div className="sparky-fx sparky-fx--magnifier">
        <svg className="glass" viewBox="0 0 120 120">
          <line x1="72" y1="72" x2="108" y2="108" stroke="#5e3f22" strokeWidth="15" strokeLinecap="round" />
          <circle cx="48" cy="48" r="40" fill="rgba(150,205,235,.28)" />
          <circle cx="48" cy="48" r="40" fill="none" stroke="#caa44a" strokeWidth="9" />
          <path
            d="M26 38 A26 26 0 0 1 58 22"
            stroke="#fff"
            strokeWidth="6"
            fill="none"
            strokeLinecap="round"
            opacity=".8"
          />
        </svg>
      </div>
    </div>
  )
}
